import { useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { useStickyAgent } from "../../../../hooks/useStickyAgent";
import { useTrackEvent } from "../../../../hooks/useTrackEvent";
import useToggle from "../../../../hooks/useToggle";
import {
    useGetAgentListingsQuery,
    useGetAgentQuery,
} from "../../../../services/agentEndpoints";

/**
 * Custom hook for the Agent page.
 * @returns Functions and logic used by the Agent page.
 */
export const useAgent = () => {
    const { agent: agentSlug } = useParams();
    const isEntitled = useSelector((state) => state.root.isEntitled);
    const { execute: setStickyAgent } = useStickyAgent();
    const { trackEvent, trackGTM } = useTrackEvent();
    const { data, refetch, isLoading } = useGetAgentQuery({ agentSlug });
    const {
        agentId,
        agentInfo,
        agentAreasServed,
        agentMedia,
        crossNiche,
        teamInfo,
    } = data || {};
    const { data: agentListings = [] } = useGetAgentListingsQuery(
        { agentId },
        { skip: !agentId }
    );
    const rawMicroContent =
        data?.microContent && data?.microContent !== ""
            ? data?.microContent
            : [];

    const modifiedAgentAreas = (agentAreasServed || []).map((area) => ({
        ...area,
        children: (area.children || []).map((child) => {
            const { listings, ...rest } = child.more || {};
            return {
                ...child,
                more: rest,
            };
        }),
    }));

    const handlePhoneClick = () => {
        trackGTM({
            event: `phoneClick`,
            action: "click",
            type: "Agent",
            category: "contact",
            button_label: agent_website_phone?.value,
            value: agent_website_phone?.value,
            ...data,
        });
        trackEvent("ACTIONS", {
            CATEGORY: "event",
            ASSOCIATE: "Agent Phone",
            ACTION: "click",
            SOURCE_INFO: {
                eventLabel: agent_name?.value,
                eventValue: agent_website_phone?.value,
            },
        });
    };

    const publishAgentContent = async () => {
        refetch();
    };

    const {
        agent_name,
        agentType,
        isTeam,
        agentTitle_part1,
        agentTitle_part2,
        brokerageName,
        agent_pic,
        is_active,
        parentAgentName,
        parentAgentUri,
        agent_description,
        agent_website_phone,
    } = agentInfo || {};
    const isTeamMember =
        agentType?.value === "member" && agentInfo?.parentAgentId;

    const agentAdjective = isTeam?.value === true ? "Our" : "My";
    const hasTeam = isTeam && teamInfo.length > 0;

    const isActive = is_active?.value;
    const handleAgentAreaClick = async () => {
        await setStickyAgent(agentId);
    };
    const firstName = agent_name?.value.split(" ")[0];
    const [isOpen, toggleIsOpen] = useToggle(false);

    // Turn rawMicroContent into an object with the keys from the item property
    const microContent = rawMicroContent.reduce((acc, item) => {
        acc[item.item] = item;
        return acc;
    }, {});

    return {
        agent: data,
        isLoading,
        agent_pic,
        agent_name,
        agentTitle_part1,
        agentTitle_part2,
        isEntitled,
        brokerageName,
        agent_website_phone,
        firstName,
        agentName: agent_name?.value || "", // getting agent name as string
        agentId,
        agentMedia,
        agent_description,
        modifiedAgentAreas,
        handleAgentAreaClick,
        internalAgentID: agentId,
        isOpen,
        isActive,
        toggleIsOpen,
        handlePhoneClick,
        microContent,
        publishAgentContent,
        agentListings,
        crossNiche,
        teamInfo,
        hasTeam,
        parentAgentName,
        parentAgentUri,
        isTeamMember,
        agentAdjective,
    };
};
